import { motion } from "motion/react";
import { type ReactNode, Suspense, lazy, useEffect } from "react";

import { useHasCesiumToken, useKeysStore } from "@/stores/keys";
import { ErrorBoundary } from "./ErrorBoundary";
import { LeftRail } from "./LeftRail";
import { TopBar } from "./TopBar";

// Globe is lazy-loaded so the initial JS bundle stays slim. It mounts once
// at the AppShell level and lives on across route changes.
const WildfireGlobe = lazy(() =>
  import("@/features/globe/WildfireGlobe").then((m) => ({ default: m.WildfireGlobe })),
);

// The assistant lives above every route so a conversation survives being
// navigated — which it has to, since the assistant can navigate the user.
const AssistantDock = lazy(() =>
  import("@/features/assistant/AssistantDock").then((m) => ({ default: m.AssistantDock })),
);

export function AppShell({ children }: { children: ReactNode }) {
  const showGlobe = useHasCesiumToken();

  // Ask the backend which keys are set, once per load. Read-only: it sends
  // nothing and can write nothing. The "this feature needs a key" notices
  // stay silent until this answers.
  useEffect(() => {
    void useKeysStore.getState().sync();
  }, []);

  return (
    <div
      className="app-shell"
      style={{
        position: "fixed",
        inset: 0,
        display: "grid",
        gridTemplateColumns: "72px 1fr",
        gridTemplateRows: "56px 1fr",
        gridTemplateAreas: `"rail top" "rail main"`,
      }}
    >
      <motion.div
        style={{ gridArea: "rail", zIndex: 10 }}
        initial={{ x: -32, opacity: 0 }}
        animate={{ x: 0, opacity: 1 }}
        transition={{ duration: 0.6, delay: 0.9, ease: [0.16, 1, 0.3, 1] }}
      >
        <LeftRail />
      </motion.div>

      <motion.div
        style={{ gridArea: "top", zIndex: 10 }}
        initial={{ y: -16, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.6, delay: 0.9, ease: [0.16, 1, 0.3, 1] }}
      >
        <TopBar />
      </motion.div>

      <main
        style={{
          gridArea: "main",
          position: "relative",
          overflow: "hidden",
          background: "var(--color-bg-0)",
        }}
      >
        {/* The Cesium viewer lives here, always mounted, behind everything. */}
        {showGlobe && (
          <ErrorBoundary label="WildfireGlobe">
            <Suspense fallback={null}>
              <WildfireGlobe />
            </Suspense>
          </ErrorBoundary>
        )}

        {/* Route content overlays. */}
        <div style={{ position: "absolute", inset: 0, pointerEvents: "none" }}>
          {/* The "none" container lets clicks pass through to the globe by
              default; each overlay re-enables pointer events on itself. */}
          {children}
        </div>

        <ErrorBoundary label="Assistant">
          <Suspense fallback={null}>
            <AssistantDock />
          </Suspense>
        </ErrorBoundary>
      </main>

      <style>{`
        /* ── Phone and small tablet ──────────────────────────────────────
           The rail moves from a 72px left column to a bottom tab bar. Two
           reasons: a fixed 72px column is a fifth of a 375px viewport, and
           a vertical icon strip sits at the top-left of the screen, which
           is the hardest place to reach with a thumb.

           Only the grid areas are reordered — the DOM order is untouched,
           so reading order and tab order stay as they are on desktop.

           The rail row adds env(safe-area-inset-bottom) for the iPhone home
           indicator; index.html already sets viewport-fit=cover, which is
           what makes that inset resolve to a real value rather than 0. */
        @media (max-width: 768px) {
          .app-shell {
            grid-template-columns: 1fr !important;
            grid-template-rows: 52px 1fr calc(58px + env(safe-area-inset-bottom, 0px)) !important;
            grid-template-areas: "top" "main" "rail" !important;
          }
        }
      `}</style>
    </div>
  );
}
